/**
 * AI CONTEXT
 * Purpose: pure helpers for the student profile Standout Moments and TA
 * Journey tabs — whether the TA tab exists, first-name parent copy, and
 * which moments can be approved for the family.
 * Used by: StudentProfileTabs, StudentStandoutMomentsTab, StudentTaJourneyTab.
 */
import dayjs from "dayjs"
import { groupBy, orderBy } from "lodash"

import {
  type FamilyReactionEntry,
  type FamilyState,
  type StandoutCategory,
  type StandoutMediaEntry,
} from "#customTypes/composedTypes/standoutTypes"
import { type Attendance } from "#customTypes/types"
import {
  deriveFamilyState,
  familySharePatch,
  NO_CLASS_SESSION_LABEL,
  type SentNoteFields,
  sentNoteSharePatch,
} from "#utils/standoutMomentMigration"

export { NO_CLASS_SESSION_LABEL }

/** True when this student has at least one assistant-attendance session. */
export const studentHasAssistantAttendance = (
  assistantAttendance: Attendance | null | undefined
): boolean => {
  if (assistantAttendance == null) return false
  return Object.values(assistantAttendance).some(
    (sessions) => Array.isArray(sessions) && sessions.length > 0
  )
}

/**
 * First name only. Uses the stored firstName, otherwise the first token of
 * the display name. Empty when neither is present — callers must not
 * substitute a hardcoded name.
 */
export const composeStudentFirstName = (
  student:
    | { firstName?: string | null; name?: string | null }
    | null
    | undefined
): string => {
  const fromFirst = student?.firstName?.trim().split(/\s+/)[0]
  if (fromFirst) return fromFirst
  const fromName = student?.name?.trim().split(/\s+/)[0]
  return fromName ?? ""
}

/** TA Journey copy. Other surfaces use the shorter labels below. */
export const taJourneyFamilyCopy = (
  firstName: string
): { sent: string; reacted: string } => ({
  sent: `Sent to ${firstName}'s parents — good job!`,
  reacted: `${firstName}'s family reacted`,
})

export const SENT_TO_PARENTS_LABEL = "Sent to parents"
export const FAMILY_REACTED_LABEL = "Family reacted"

export const momentHasFamilyReaction = (moment: {
  familyReactions?: Record<string, FamilyReactionEntry | null> | null
}): boolean =>
  Object.values(moment.familyReactions ?? {}).some(
    (entry) => entry != null && entry.types.length > 0
  )

/** Pending, non-secret rows that have a class session. Approved, sent, secret, and session-less rows are not selectable. */
export const isPendingFamilyApproval = (moment: {
  category?: StandoutCategory
  familyState?: FamilyState
  isShared?: boolean
  classSessionId?: string | null
  dontShare?: boolean
}): boolean =>
  moment.category !== "secret" &&
  moment.dontShare !== true &&
  Boolean(moment.classSessionId) &&
  deriveFamilyState(moment) === "pending"

/** Staff TA Journey matches the family wire: secret and don't-share moments stay out of the list and the counts. */
export const withoutSecretMoments = <
  T extends { category?: StandoutCategory; dontShare?: boolean },
>(
  moments: readonly T[]
): T[] =>
  moments.filter(
    (moment) => moment.category !== "secret" && moment.dontShare !== true
  )

export const buildFamilyApprovePatch = (
  sharedBy: string,
  sharedAt: string,
  existingSharedBy?: string | null,
  moment?: SentNoteFields | null
) => ({
  ...familySharePatch("approved", sharedBy, sharedAt, existingSharedBy),
  ...(moment ? sentNoteSharePatch(moment) : {}),
})

/** Uncheck an approved row. sharedAt and sharedBy stay so a later sweep still counts a send. */
export const buildFamilyRevokePatch = (
  sharedBy: string,
  sharedAt: string,
  existingSharedBy?: string | null
) => familySharePatch("pending", sharedBy, sharedAt, existingSharedBy)

/**
 * Byline for a profile story. A TA-written row uses that student's name.
 * A staff row uses the user who saved it.
 */
export const standoutAuthorName = (
  moment: { createdBy?: string | null; assistantId?: string | null },
  usersById: Record<string, { name?: string | null } | undefined>,
  studentsById: Record<
    string,
    { firstName?: string | null; lastName?: string | null } | undefined
  >
): string => {
  const assistantId = moment.assistantId?.trim()
  if (assistantId) {
    const student = studentsById[assistantId]
    const name = [student?.firstName, student?.lastName]
      .filter((part) => part && part.trim())
      .join(" ")
    return name || "TA"
  }
  const teacher = moment.createdBy
    ? usersById[moment.createdBy]?.name?.trim()
    : ""
  return teacher || "Teacher"
}

export type StandoutMomentTextLine = {
  key: "note" | "generatedNote" | "generatedText"
  label: "Teacher note" | "TA note" | "Generated" | "Generated (eval)"
  text: string
}

const trimmedText = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/**
 * The three write-ups are separate signals. A missing teacher note does not
 * hide generatedNote or generatedText, and a media-only story still has
 * photos and audio to show.
 */
export const standoutMomentContent = (moment: {
  note?: string | null
  generatedNote?: string | null
  generatedText?: string | null
  media?: StandoutMediaEntry[] | null
  assistantId?: string | null
}): {
  lines: StandoutMomentTextLine[]
  photos: StandoutMediaEntry[]
  audio: StandoutMediaEntry[]
} => {
  const lines: StandoutMomentTextLine[] = []
  const note = trimmedText(moment.note)
  const generatedNote = trimmedText(moment.generatedNote)
  const generatedText = trimmedText(moment.generatedText)
  if (note) {
    lines.push({
      key: "note",
      label: moment.assistantId?.trim() ? "TA note" : "Teacher note",
      text: note,
    })
  }
  if (generatedNote) {
    lines.push({
      key: "generatedNote",
      label: "Generated",
      text: generatedNote,
    })
  }
  if (generatedText) {
    lines.push({
      key: "generatedText",
      label: "Generated (eval)",
      text: generatedText,
    })
  }
  const media = moment.media ?? []
  return {
    lines,
    photos: media.filter((entry) => entry.kind === "photo"),
    audio: media.filter((entry) => entry.kind === "audio"),
  }
}

/** mm:ss for a clip length. Empty when the value is not a usable duration. */
export const formatMediaDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return ""
  const total = Math.round(seconds)
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

export const countSentMoments = (
  moments: Array<{ familyState?: FamilyState; isShared?: boolean }>
): number =>
  moments.filter((moment) => deriveFamilyState(moment) === "sent").length

export type MomentDayGroup<T extends { createdAt: string }> = {
  dayKey: string
  label: string
  moments: T[]
}

export const groupMomentsByDay = <T extends { createdAt: string }>(
  moments: T[]
): Array<MomentDayGroup<T>> => {
  const grouped = groupBy(moments, (moment) =>
    dayjs(moment.createdAt).format("YYYY-MM-DD")
  )
  return orderBy(Object.keys(grouped), (dayKey) => dayKey, "desc").map(
    (dayKey) => ({
      dayKey,
      label: dayjs(dayKey).format("M/D/YY"),
      moments: orderBy(
        grouped[dayKey] ?? [],
        (moment) => moment.createdAt,
        "desc"
      ),
    })
  )
}
